import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { ConfirmDangerModal } from '../components/ConfirmDangerModal'
import { EscolasSection } from '../components/escolas/EscolasSection'
import { EscolasCeactSection } from '../components/escolas/EscolasCeactSection'
import { FornecedoresSection } from '../components/fornecedores/FornecedoresSection'
import { ConfirmModal } from '../components/ConfirmModal'
import { IconButton } from '../components/IconButton'
import { Modal } from '../components/Modal'
import { MacronutrientesSection } from '../components/config/MacronutrientesSection'
import { ValoresReferenciaSection } from '../components/config/ValoresReferenciaSection'
import { SortableTh } from '../components/SortableTh'
import { useTableSort } from '../hooks/useTableSort'
import { groupPrecosPorProduto, resolvePrecoVigente, type PrecoGrupo } from '../lib/precoVigente'
import {
  formatGramas,
  formatPreco100g,
  formatUnidadeExibicao,
  gramasEquivalenteUnidadeComercial,
  isUnidadeMaco,
  isUnidadeMl,
  modoConversaoUnidade,
  type ModoConversaoUnidade,
  precoPor100g,
  sugerirGramasPorUnidade,
  textoConversaoGramas,
  totalGramasEmbalagem,
  UNIDADES_COMERCIAIS,
} from '../lib/produtoConversaoKg'
import { supabase } from '../lib/supabase'
import { downloadText, formatCurrencyInput, formatDate, parseCurrencyBr, todayISO } from '../lib/utils'
import type {
  Configuracoes,
  Escola,
  Fornecedor,
  PerfilUsuario,
  Preco,
  Produto,
  Rota,
  Usuario,
  ZonaTipo,
} from '../types/database'
import { PERFIL_LABELS } from '../types/database'

type Tab =
  | 'geral'
  | 'macronutrientes'
  | 'valores_referencia'
  | 'precos'
  | 'rotas'
  | 'fornecedores'
  | 'backup'
  | 'escolas'
  | 'escolas_ceact'
  | 'usuarios'

const TAB_IDS: Tab[] = [
  'geral',
  'macronutrientes',
  'valores_referencia',
  'precos',
  'rotas',
  'fornecedores',
  'backup',
  'escolas',
  'escolas_ceact',
  'usuarios',
]

function parseTabParam(value: string | null): Tab {
  if (value === 'produtos' || value === 'per_capita') return 'macronutrientes'
  if (value && TAB_IDS.includes(value as Tab)) return value as Tab
  return 'geral'
}

type ConversaoDraft = {
  unidade: string
  gramas_por_unidade: string
  unidades_por_embalagem: string
  gramas_por_item: string
}

function parseNumeroConversao(s: string | undefined): number | null {
  if (s === undefined || s === '') return null
  const n = Number(String(s).replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? n : null
}

function conversaoDraftFromProduto(p: Produto): ConversaoDraft {
  let gramas = p.gramas_por_unidade
  if ((gramas == null || gramas <= 0) && p.ml_por_unidade && p.gramas_por_ml) {
    gramas = p.ml_por_unidade * p.gramas_por_ml
  }
  if (gramas == null || gramas <= 0) {
    gramas = sugerirGramasPorUnidade(p.unidade)
  }
  return {
    unidade: p.unidade,
    gramas_por_unidade: gramas != null && gramas > 0 ? String(gramas) : '',
    unidades_por_embalagem:
      p.unidades_por_embalagem != null && p.unidades_por_embalagem > 0
        ? String(p.unidades_por_embalagem)
        : '',
    gramas_por_item:
      p.gramas_por_item != null && p.gramas_por_item > 0 ? String(p.gramas_por_item) : '',
  }
}

function modoConversaoDoDraft(produto: Produto, draft?: ConversaoDraft): ModoConversaoUnidade {
  const unidade = (draft?.unidade ?? produto.unidade).trim()
  const qtd =
    draft?.unidades_por_embalagem !== undefined && draft.unidades_por_embalagem !== ''
      ? parseNumeroConversao(draft.unidades_por_embalagem)
      : produto.unidades_por_embalagem ?? null
  return modoConversaoUnidade(unidade, { unidades_por_embalagem: qtd })
}

function conversaoEfetiva(produto: Produto, draft?: ConversaoDraft) {
  const unidade = (draft?.unidade ?? produto.unidade).trim()

  if (modoConversaoDoDraft(produto, draft) === 'embalagem') {
    const qtd =
      draft?.unidades_por_embalagem !== undefined
        ? parseNumeroConversao(draft.unidades_por_embalagem)
        : produto.unidades_por_embalagem ?? null
    const peso =
      draft?.gramas_por_item !== undefined
        ? parseNumeroConversao(draft.gramas_por_item)
        : produto.gramas_por_item ?? null
    const total = totalGramasEmbalagem(qtd, peso) ?? produto.gramas_por_unidade ?? null
    return {
      unidade,
      unidades_por_embalagem: qtd,
      gramas_por_item: peso,
      gramas_por_unidade: total,
      ml_por_unidade: produto.ml_por_unidade,
      gramas_por_ml: produto.gramas_por_ml,
    }
  }

  const gramas =
    draft?.gramas_por_unidade !== undefined && draft.gramas_por_unidade !== ''
      ? parseNumeroConversao(draft.gramas_por_unidade)
      : produto.gramas_por_unidade ?? null
  return {
    unidade,
    unidades_por_embalagem: produto.unidades_por_embalagem,
    gramas_por_item: produto.gramas_por_item,
    gramas_por_unidade: gramas,
    ml_por_unidade: produto.ml_por_unidade,
    gramas_por_ml: produto.gramas_por_ml,
  }
}

function totalGramasDoDraft(produto: Produto, draft: ConversaoDraft): number | null {
  if (modoConversaoDoDraft(produto, draft) === 'embalagem') {
    return totalGramasEmbalagem(
      parseNumeroConversao(draft.unidades_por_embalagem),
      parseNumeroConversao(draft.gramas_por_item),
    )
  }
  return parseNumeroConversao(draft.gramas_por_unidade)
}

function textoPreco100g(
  produto: Produto | undefined,
  valorUnitario: number | null | undefined,
  draft?: ConversaoDraft,
) {
  if (!produto || valorUnitario == null) return '—'
  const gramas = gramasEquivalenteUnidadeComercial(conversaoEfetiva(produto, draft))
  if (!gramas) return 'Informe gramas'
  return formatPreco100g(precoPor100g(valorUnitario, gramas))
}

export function ConfiguracoesPage() {
  const { isAdmin, profile, canDelete, canWrite, isLocalDev } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>(() => parseTabParam(searchParams.get('tab')))
  const [cfg, setCfg] = useState<Configuracoes | null>(null)
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [precos, setPrecos] = useState<(Preco & { produto?: { nome: string } })[]>([])
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([])
  const [produtoModalOpen, setProdutoModalOpen] = useState(false)
  const [savingProduto, setSavingProduto] = useState(false)
  const [produtoForm, setProdutoForm] = useState({ nome: '', fornecedor_id: '' })
  const [rotas, setRotas] = useState<Rota[]>([])
  const [escolasCfg, setEscolasCfg] = useState<Escola[]>([])
  const [rotaEscolaIds, setRotaEscolaIds] = useState<string[]>([])
  const [usuarios, setUsuarios] = useState<Usuario[]>([])
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [dangerAction, setDangerAction] = useState<string | null>(null)
  const [editingRota, setEditingRota] = useState<Rota | null>(null)
  const [rotaModalOpen, setRotaModalOpen] = useState(false)
  const [rotaEscolaFiltro, setRotaEscolaFiltro] = useState('')
  const [savingRota, setSavingRota] = useState(false)
  const [rotaExpandida, setRotaExpandida] = useState<string | null>(null)
  const [precosExpandidos, setPrecosExpandidos] = useState<Set<string>>(new Set())
  const [deleteRotaTarget, setDeleteRotaTarget] = useState<Rota | null>(null)
  type PrecoRow = Preco & { produto?: { nome: string } }
  type PrecoGrupoRow = PrecoGrupo<PrecoRow>
  const precosSort = useTableSort<PrecoGrupoRow>('produto', 'asc')
  const rotasSort = useTableSort<Rota>('nome', 'asc')
  const usuariosSort = useTableSort<Usuario>('nome', 'asc')
  const [precoForm, setPrecoForm] = useState({
    produto_id: '',
    valor_unitario: '',
    vigencia_inicio: todayISO(),
    fornecedor: '',
  })
  const [rotaForm, setRotaForm] = useState({
    nome: '',
    responsavel: '',
    zona: 'urbana' as ZonaTipo,
    nutricionista_id: '',
  })
  const [invite, setInvite] = useState({
    email: '',
    nome: '',
    senha: '',
    perfil: 'fiscal' as PerfilUsuario,
  })
  const [inviting, setInviting] = useState(false)
  const [editUsuario, setEditUsuario] = useState<Usuario | null>(null)
  const [editForm, setEditForm] = useState({
    nome: '',
    perfil: 'fiscal' as PerfilUsuario,
    ativo: true,
  })
  const [savingUsuario, setSavingUsuario] = useState(false)
  const [resetUsuario, setResetUsuario] = useState<Usuario | null>(null)
  const [resetForm, setResetForm] = useState({ senha: '', confirmar: '' })
  const [resettingSenha, setResettingSenha] = useState(false)
  const [conversaoDraft, setConversaoDraft] = useState<Record<string, ConversaoDraft>>({})

  const produtoById = useMemo(() => {
    const map = new Map<string, Produto>()
    for (const p of produtos) map.set(p.id, p)
    return map
  }, [produtos])

  const load = useCallback(async () => {
    if (!isAdmin) return
    const [c, p, pr, f, r, esc, u] = await Promise.all([
      supabase.from('configuracoes').select('*').eq('id', 1).maybeSingle(),
      supabase.from('produtos').select('*').order('nome'),
      supabase
        .from('precos')
        .select('*, produto:produtos(nome)')
        .order('criado_em', { ascending: true }),
      supabase.from('fornecedores').select('*').eq('ativo', true).order('nome'),
      supabase.from('rotas').select('*, nutricionista:usuarios(nome)').order('nome'),
      supabase.from('escolas').select('*').order('nome'),
      supabase.from('usuarios').select('*').order('nome'),
    ])
    const firstError = [c, p, pr, f, r, esc, u].find((res) => res.error)?.error
    if (firstError) setError(firstError.message)
    setCfg((c.data as Configuracoes) || null)
    setProdutos((p.data as Produto[]) || [])
    setPrecos((pr.data as typeof precos) || [])
    setFornecedores((f.data as Fornecedor[]) || [])
    setRotas((r.data as Rota[]) || [])
    setEscolasCfg((esc.data as Escola[]) || [])
    setUsuarios((u.data as Usuario[]) || [])
  }, [isAdmin])

  useEffect(() => {
    document.title = 'Configurações · ISEN EDUCAE'
    load()
  }, [load])

  const precosGrouped = useMemo(() => {
    const hoje = todayISO()
    const groups = groupPrecosPorProduto(precos, hoje).map((g) => {
      const produto = produtoById.get(g.produtoId)
      return {
        ...g,
        produtoNome: produto?.nome ?? g.produtoNome,
      }
    })
    const accessors: Record<string, (g: PrecoGrupoRow) => string | number> = {
      produto: (g) => g.produtoNome,
      unidade: (g) => {
        const p = produtoById.get(g.produtoId)
        return p ? formatUnidadeExibicao(p.unidade) : ''
      },
      gramas: (g) => {
        const p = produtoById.get(g.produtoId)
        if (!p) return -1
        return gramasEquivalenteUnidadeComercial(p) ?? -1
      },
      valor: (g) => Number(g.valido?.valor_unitario ?? 0),
      preco100g: (g) => {
        const p = produtoById.get(g.produtoId)
        if (!p || !g.valido) return -1
        const gramas = gramasEquivalenteUnidadeComercial(p)
        if (!gramas) return -1
        return precoPor100g(Number(g.valido.valor_unitario), gramas) ?? -1
      },
      vigencia: (g) => g.valido?.vigencia_inicio ?? '',
      fornecedor: (g) => g.valido?.fornecedor ?? '',
    }
    return precosSort.sortRows(groups, accessors)
  }, [precos, precosSort, produtoById])

  const escolasCountByRota = useMemo(() => {
    const map = new Map<string, number>()
    for (const e of escolasCfg) {
      if (!e.rota_id) continue
      map.set(e.rota_id, (map.get(e.rota_id) || 0) + 1)
    }
    return map
  }, [escolasCfg])

  const rotaNomeById = useMemo(() => {
    const map = new Map<string, string>()
    for (const r of rotas) map.set(r.id, r.nome)
    return map
  }, [rotas])

  const nutricionistas = useMemo(
    () => usuarios.filter((u) => u.ativo && u.perfil === 'nutricionista'),
    [usuarios],
  )

  const rotasSorted = useMemo(() => {
    const accessors: Record<string, (r: Rota) => string | number> = {
      nome: (r) => r.nome,
      responsavel: (r) => r.nutricionista?.nome || r.responsavel,
      zona: (r) => r.zona,
      escolas: (r) => escolasCountByRota.get(r.id) || 0,
    }
    return rotasSort.sortRows(rotas, accessors)
  }, [rotas, rotasSort, escolasCountByRota])

  const escolasFiltradasRota = useMemo(() => {
    const q = rotaEscolaFiltro.trim().toLowerCase()
    if (!q) return escolasCfg
    return escolasCfg.filter((e) => e.nome.toLowerCase().includes(q))
  }, [escolasCfg, rotaEscolaFiltro])

  const usuariosSorted = useMemo(() => {
    const accessors: Record<string, (u: Usuario) => string | number> = {
      nome: (u) => u.nome,
      email: (u) => u.email,
      perfil: (u) => u.perfil,
      ativo: (u) => (u.ativo ? 1 : 0),
    }
    return usuariosSort.sortRows(usuarios, accessors)
  }, [usuarios, usuariosSort])

  function resolveFornecedorPreco(atual?: PrecoRow | null): {
    fornecedor: string | null
    fornecedor_id: string | null
  } {
    const nomeAtual = atual?.fornecedor?.trim()
    if (nomeAtual) {
      return { fornecedor: nomeAtual, fornecedor_id: atual?.fornecedor_id ?? null }
    }
    if (atual?.fornecedor_id) {
      const f = fornecedores.find((x) => x.id === atual.fornecedor_id)
      if (f) {
        return {
          fornecedor: f.nome || f.razao_social,
          fornecedor_id: f.id,
        }
      }
    }
    return {
      fornecedor: cfg?.fornecedor_atual?.trim() || null,
      fornecedor_id: null,
    }
  }

  async function insertPrecoHistorico(
    produtoId: string,
    valor: number,
    vigenciaInicio: string,
    atual?: PrecoRow | null,
  ) {
    if (!canWrite('precos')) return
    if (valor <= 0) {
      setError('Informe um valor válido.')
      return
    }
    if (!vigenciaInicio) {
      setError('Informe a vigência.')
      return
    }
    const fornecedorRef = resolveFornecedorPreco(atual)
    const { error: err } = await supabase.from('precos').insert({
      produto_id: produtoId,
      valor_unitario: valor,
      vigencia_inicio: vigenciaInicio,
      fornecedor: fornecedorRef.fornecedor,
      fornecedor_id: fornecedorRef.fornecedor_id,
    })
    if (err) setError(err.message)
    else {
      setInfo('Novo registro de preço adicionado ao histórico.')
      load()
    }
  }

  function togglePrecoExpandido(produtoId: string) {
    setPrecosExpandidos((prev) => {
      const next = new Set(prev)
      if (next.has(produtoId)) next.delete(produtoId)
      else next.add(produtoId)
      return next
    })
  }

  async function onPrecoVigenteBlur(
    produtoId: string,
    valido: PrecoRow | null,
    valorStr: string,
    vigenciaStr: string,
  ) {
    const valor = parseCurrencyBr(valorStr)
    const vigencia = vigenciaStr
    if (
      valido &&
      valor === Number(valido.valor_unitario) &&
      vigencia === valido.vigencia_inicio
    ) {
      return
    }
    await insertPrecoHistorico(produtoId, valor, vigencia, valido)
  }

  async function onFornecedorVigenteChange(
    produtoId: string,
    valido: PrecoRow | null,
    fornecedorId: string,
  ) {
    if (!canWrite('precos')) return

    const atualId = valido?.fornecedor_id ?? ''
    if (fornecedorId === atualId) return

    let fornecedorNome: string | null = null
    let fornecedorUuid: string | null = null
    if (fornecedorId) {
      const fornecedor = fornecedores.find((f) => f.id === fornecedorId)
      if (!fornecedor) {
        setError('Fornecedor inválido.')
        return
      }
      fornecedorNome = (fornecedor.razao_social || fornecedor.nome || '').trim() || null
      fornecedorUuid = fornecedor.id
    }

    setError(null)
    if (!valido) {
      const { error: err } = await supabase.from('precos').insert({
        produto_id: produtoId,
        valor_unitario: 0,
        vigencia_inicio: todayISO(),
        fornecedor: fornecedorNome,
        fornecedor_id: fornecedorUuid,
      })
      if (err) {
        setError(err.message)
        return
      }
    } else {
      const { error: err } = await supabase
        .from('precos')
        .update({
          fornecedor: fornecedorNome,
          fornecedor_id: fornecedorUuid,
        })
        .eq('id', valido.id)
      if (err) {
        setError(err.message)
        return
      }
    }
    load()
  }

  function getConversaoDraft(produto: Produto): ConversaoDraft {
    return conversaoDraft[produto.id] ?? conversaoDraftFromProduto(produto)
  }

  function patchConversaoDraft(produtoId: string, patch: Partial<ConversaoDraft>) {
    const produto = produtoById.get(produtoId)
    if (!produto) return
    setConversaoDraft((prev) => {
      const base = { ...conversaoDraftFromProduto(produto), ...prev[produtoId] }
      const next = { ...base, ...patch }
      if (patch.unidade != null) {
        const modoAnterior = modoConversaoDoDraft(produto, base)
        const modoNovo = modoConversaoDoDraft(produto, next)
        if (modoNovo !== modoAnterior) {
          next.unidades_por_embalagem = ''
          next.gramas_por_item = ''
          next.gramas_por_unidade = ''
        }
        if (modoNovo === 'direta') {
          const sugerido = sugerirGramasPorUnidade(patch.unidade)
          if (sugerido != null) next.gramas_por_unidade = String(sugerido)
        }
      }
      return { ...prev, [produtoId]: next }
    })
  }

  async function saveConversaoProduto(produtoId: string, silent = false) {
    const produto = produtoById.get(produtoId)
    if (!produto || !canWrite('precos')) return false

    const draft = getConversaoDraft(produto)
    const unidade = draft.unidade.trim()

    if (!unidade) {
      if (!silent) setError('Informe a unidade comercial.')
      return false
    }

    let payload: Partial<Produto>

    if (modoConversaoDoDraft(produto, draft) === 'embalagem') {
      const qtd = parseNumeroConversao(draft.unidades_por_embalagem)
      const peso = parseNumeroConversao(draft.gramas_por_item)
      if (!(qtd != null && qtd > 0)) {
        if (!silent) setError('Informe a quantidade de unidades na embalagem (ex.: 30 ovos por cartela).')
        return false
      }
      if (!(peso != null && peso > 0)) {
        if (!silent) setError('Informe o peso em gramas de cada unidade (ex.: 50 g por ovo).')
        return false
      }
      payload = {
        unidade,
        unidades_por_embalagem: qtd,
        gramas_por_item: peso,
        gramas_por_unidade: qtd * peso,
        ml_por_unidade: null,
        gramas_por_ml: null,
      }
    } else if (isUnidadeMaco(unidade)) {
      const g = parseNumeroConversao(draft.gramas_por_unidade)
      if (!(g != null && g > 0)) {
        if (!silent) setError('Informe a quantidade em gramas de 1 maço.')
        return false
      }
      payload = {
        unidade,
        gramas_por_unidade: g,
        unidades_por_embalagem: null,
        gramas_por_item: null,
        ml_por_unidade: null,
        gramas_por_ml: null,
      }
    } else {
      const g = parseNumeroConversao(draft.gramas_por_unidade)
      if (!(g != null && g > 0)) {
        if (!silent) setError('Informe as gramas por unidade comercial.')
        return false
      }
      payload = {
        unidade,
        gramas_por_unidade: g,
        unidades_por_embalagem: null,
        gramas_por_item: null,
        ml_por_unidade: isUnidadeMl(unidade) ? g : null,
        gramas_por_ml: isUnidadeMl(unidade) ? 1 : null,
      }
    }

    if (!silent) setError(null)
    const { error: err } = await supabase.from('produtos').update(payload).eq('id', produtoId)
    if (err) {
      if (!silent) setError(err.message)
      return false
    }
    setProdutos((prev) =>
      prev.map((p) => (p.id === produtoId ? { ...p, ...payload } : p)),
    )
    setConversaoDraft((prev) => {
      const next = { ...prev }
      delete next[produtoId]
      return next
    })
    return true
  }

  function onConversaoBlur(produtoId: string) {
    void saveConversaoProduto(produtoId, true)
  }

  useEffect(() => {
    const fromUrl = parseTabParam(searchParams.get('tab'))
    setTab((current) => (current === fromUrl ? current : fromUrl))
  }, [searchParams])

  if (!isAdmin) {
    return <div className="alert alert-error">Acesso restrito a administradores.</div>
  }

  async function saveCfg() {
    const payload = {
      id: 1 as const,
      municipio: cfg?.municipio || null,
      estado: cfg?.estado || null,
      secretaria: cfg?.secretaria || null,
      nutricionista_responsavel: cfg?.nutricionista_responsavel || null,
      ano_letivo: cfg?.ano_letivo || 2026,
      fiscal_contrato: cfg?.fiscal_contrato || null,
      fornecedor_atual: cfg?.fornecedor_atual || null,
      cnpj_fornecedor: cfg?.cnpj_fornecedor || null,
    }
    const { error: err } = await supabase.from('configuracoes').upsert(payload)
    if (err) setError(err.message)
    else load()
  }

  async function savePreco() {
    if (!precoForm.produto_id) {
      setError('Selecione um produto.')
      return
    }
    const valor = parseCurrencyBr(precoForm.valor_unitario)
    if (valor <= 0) {
      setError('Informe um valor válido.')
      return
    }
    const hojeIso = todayISO()
    const atualProduto = resolvePrecoVigente(
      precos.filter((p) => p.produto_id === precoForm.produto_id),
      hojeIso,
    )
    const fornecedorRef = resolveFornecedorPreco(atualProduto)
    const fornecedorManual = precoForm.fornecedor.trim()
    const { error: err } = await supabase.from('precos').insert({
      produto_id: precoForm.produto_id,
      valor_unitario: valor,
      vigencia_inicio: precoForm.vigencia_inicio,
      fornecedor: fornecedorManual || fornecedorRef.fornecedor,
      fornecedor_id: fornecedorManual ? null : fornecedorRef.fornecedor_id,
    })
    if (err) setError(err.message)
    else {
      setPrecoForm({
        produto_id: '',
        valor_unitario: '',
        vigencia_inicio: todayISO(),
        fornecedor: '',
      })
      setInfo('Preço adicionado.')
      load()
    }
  }

  function openNovoProdutoModal() {
    setError(null)
    setProdutoForm({ nome: '', fornecedor_id: '' })
    setProdutoModalOpen(true)
  }

  function closeNovoProdutoModal() {
    setProdutoModalOpen(false)
    setProdutoForm({ nome: '', fornecedor_id: '' })
    setSavingProduto(false)
  }

  async function saveNovoProduto() {
    if (!canWrite('precos') || savingProduto) return
    const nome = produtoForm.nome.trim()
    if (!nome) {
      setError('Informe o nome do produto.')
      return
    }
    if (!produtoForm.fornecedor_id) {
      setError('Selecione o fornecedor.')
      return
    }
    const fornecedor = fornecedores.find((f) => f.id === produtoForm.fornecedor_id)
    if (!fornecedor) {
      setError('Fornecedor inválido.')
      return
    }
    const nomeExistente = produtos.some((p) => p.nome.trim().toLowerCase() === nome.toLowerCase())
    if (nomeExistente) {
      setError('Já existe um produto com este nome.')
      return
    }

    setSavingProduto(true)
    setError(null)
    const { data: created, error: prodErr } = await supabase
      .from('produtos')
      .insert({
        nome,
        grupo: 'secos',
        unidade: 'kg',
      })
      .select('id')
      .single()

    if (prodErr || !created?.id) {
      setSavingProduto(false)
      setError(prodErr?.message || 'Não foi possível criar o produto.')
      return
    }

    const fornecedorNome = (fornecedor.razao_social || fornecedor.nome || '').trim()
    const { error: precoErr } = await supabase.from('precos').insert({
      produto_id: created.id,
      valor_unitario: 0,
      vigencia_inicio: todayISO(),
      fornecedor: fornecedorNome || null,
      fornecedor_id: fornecedor.id,
    })

    setSavingProduto(false)
    if (precoErr) {
      setError(precoErr.message)
      return
    }

    closeNovoProdutoModal()
    setInfo('Produto cadastrado. Informe o valor unitário e a conversão na tabela.')
    load()
  }

  function cancelEditRota() {
    setEditingRota(null)
    setRotaModalOpen(false)
    setRotaForm({ nome: '', responsavel: '', zona: 'urbana', nutricionista_id: '' })
    setRotaEscolaIds([])
    setRotaEscolaFiltro('')
  }

  function openCreateRota() {
    setEditingRota(null)
    setRotaForm({ nome: '', responsavel: '', zona: 'urbana', nutricionista_id: '' })
    setRotaEscolaIds([])
    setRotaEscolaFiltro('')
    setError(null)
    setRotaModalOpen(true)
  }

  function openEditRota(r: Rota) {
    setEditingRota(r)
    setRotaForm({
      nome: r.nome,
      responsavel: r.responsavel,
      zona: r.zona,
      nutricionista_id: r.nutricionista_id || '',
    })
    setRotaEscolaIds(escolasCfg.filter((e) => e.rota_id === r.id).map((e) => e.id))
    setRotaEscolaFiltro('')
    setError(null)
    setRotaModalOpen(true)
  }

  function toggleRotaEscola(escolaId: string) {
    setRotaEscolaIds((prev) =>
      prev.includes(escolaId) ? prev.filter((id) => id !== escolaId) : [...prev, escolaId],
    )
  }

  async function saveRota() {
    if (!canWrite('rotas')) return
    if (!rotaForm.nome.trim()) {
      setError('Nome da rota é obrigatório.')
      return
    }
    if (!rotaForm.nutricionista_id) {
      setError('Selecione a nutricionista responsável pela rota.')
      return
    }
    const nutri = nutricionistas.find((u) => u.id === rotaForm.nutricionista_id)
    if (!nutri) {
      setError('Nutricionista inválida. Cadastre um usuário com perfil Nutricionista.')
      return
    }
    setSavingRota(true)
    setError(null)
    const payload = {
      nome: rotaForm.nome.trim(),
      responsavel: nutri.nome,
      zona: rotaForm.zona,
      nutricionista_id: rotaForm.nutricionista_id,
    }

    let rotaId = editingRota?.id || null
    if (editingRota) {
      const { error: err } = await supabase.from('rotas').update(payload).eq('id', editingRota.id)
      if (err) {
        setSavingRota(false)
        setError(err.message)
        return
      }
    } else {
      const { data: created, error: err } = await supabase
        .from('rotas')
        .insert(payload)
        .select('id')
        .single()
      if (err || !created) {
        setSavingRota(false)
        setError(err?.message || 'Erro ao criar rota')
        return
      }
      rotaId = created.id
    }

    if (!rotaId) {
      setSavingRota(false)
      return
    }

    const selected = new Set(rotaEscolaIds)
    const previouslyOnRota = escolasCfg.filter((e) => e.rota_id === rotaId).map((e) => e.id)
    const toAssign = [...selected]
    const toClear = previouslyOnRota.filter((id) => !selected.has(id))

    if (toAssign.length) {
      const { error: errAssign } = await supabase
        .from('escolas')
        .update({ rota_id: rotaId, atualizado_em: new Date().toISOString() })
        .in('id', toAssign)
      if (errAssign) {
        setSavingRota(false)
        setError(errAssign.message)
        return
      }
    }

    if (toClear.length) {
      const { error: errClear } = await supabase
        .from('escolas')
        .update({ rota_id: null, atualizado_em: new Date().toISOString() })
        .in('id', toClear)
      if (errClear) {
        setSavingRota(false)
        setError(errClear.message)
        return
      }
    }

    setSavingRota(false)
    setInfo(editingRota ? 'Rota atualizada.' : 'Rota adicionada.')
    cancelEditRota()
    load()
  }

  async function deleteRota() {
    if (!deleteRotaTarget) return
    const { error: err } = await supabase.from('rotas').delete().eq('id', deleteRotaTarget.id)
    if (err) {
      const msg = err.message || ''
      if (err.code === '23503' || /foreign key/i.test(msg)) {
        throw new Error('Não é possível excluir: há escolas ou entregas vinculadas a esta rota.')
      }
      throw new Error(msg)
    }
    setInfo(`Rota "${deleteRotaTarget.nome}" excluída.`)
    setError(null)
    if (editingRota?.id === deleteRotaTarget.id) cancelEditRota()
    load()
  }

  async function inviteUser() {
    const nome = invite.nome.trim()
    const email = invite.email.trim().toLowerCase()
    const senha = invite.senha
    if (!nome) {
      setError('Informe o nome do usuário')
      return
    }
    if (!email || !email.includes('@')) {
      setError('Informe um e-mail válido')
      return
    }
    if (senha.length < 8) {
      setError('Senha deve ter ao menos 8 caracteres')
      return
    }

    if (isLocalDev) {
      setError(
        'Modo dev sem login Supabase: rode npm run local:users no terminal ou faça logout, login com admin@demo.educae.local e rebuild (npm run build:local).',
      )
      return
    }

    const {
      data: { session: currentSession },
    } = await supabase.auth.getSession()
    if (!currentSession?.access_token) {
      setError(
        'Sessão expirada ou inválida. Saia e entre novamente com um usuário admin (ex.: admin@demo.educae.local).',
      )
      return
    }

    setInviting(true)
    setError(null)
    setInfo(null)
    try {
      const { data, error: err } = await supabase.functions.invoke('criar-usuario', {
        body: {
          nome,
          email,
          senha,
          perfil: invite.perfil,
        },
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      })
      if (err) {
        const msg = err.message || 'Falha ao criar usuário'
        setError(
          /401|unauthorized/i.test(msg)
            ? 'Não autenticado na Edge Function. Saia, entre com admin@demo.educae.local (Demo@Timon2026) e tente de novo.'
            : msg,
        )
        return
      }
      const payload = data as { error?: string; email?: string; nome?: string } | null
      if (payload?.error) {
        setError(payload.error)
        return
      }
      setInfo(`Usuário ${payload?.nome || nome} (${payload?.email || email}) criado e já pode fazer login.`)
      setInvite({ email: '', nome: '', senha: '', perfil: 'fiscal' })
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao criar usuário')
    } finally {
      setInviting(false)
    }
  }

  async function toggleUsuario(u: Usuario) {
    const { error: err } = await supabase
      .from('usuarios')
      .update({ ativo: !u.ativo })
      .eq('id', u.id)
    if (err) setError(err.message)
    else load()
  }

  async function changePerfil(u: Usuario, perfil: PerfilUsuario) {
    const { error: err } = await supabase.from('usuarios').update({ perfil }).eq('id', u.id)
    if (err) setError(err.message)
    else load()
  }

  function openEditUsuario(u: Usuario) {
    setEditUsuario(u)
    setEditForm({ nome: u.nome, perfil: u.perfil, ativo: u.ativo })
    setError(null)
  }

  function openResetSenha(u: Usuario) {
    setResetUsuario(u)
    setResetForm({ senha: '', confirmar: '' })
    setError(null)
  }

  async function salvarUsuario() {
    if (!editUsuario) return
    const nome = editForm.nome.trim()
    if (!nome) {
      setError('Informe o nome do usuário')
      return
    }
    setSavingUsuario(true)
    setError(null)
    setInfo(null)
    try {
      const { data, error: err } = await supabase.functions.invoke('gerenciar-usuario', {
        body: {
          acao: 'atualizar',
          id: editUsuario.id,
          nome,
          perfil: editForm.perfil,
          ativo: editForm.ativo,
        },
      })
      if (err) {
        setError(err.message || 'Falha ao atualizar usuário')
        return
      }
      const payload = data as { error?: string; nome?: string } | null
      if (payload?.error) {
        setError(payload.error)
        return
      }
      setInfo(`Usuário ${payload?.nome || nome} atualizado.`)
      setEditUsuario(null)
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao atualizar usuário')
    } finally {
      setSavingUsuario(false)
    }
  }

  async function resetarSenhaUsuario() {
    if (!resetUsuario) return
    if (resetForm.senha.length < 8) {
      setError('Senha deve ter ao menos 8 caracteres')
      return
    }
    if (resetForm.senha !== resetForm.confirmar) {
      setError('A confirmação da senha não confere')
      return
    }
    setResettingSenha(true)
    setError(null)
    setInfo(null)
    try {
      const { data, error: err } = await supabase.functions.invoke('gerenciar-usuario', {
        body: {
          acao: 'resetar_senha',
          id: resetUsuario.id,
          senha: resetForm.senha,
        },
      })
      if (err) {
        setError(err.message || 'Falha ao resetar senha')
        return
      }
      const payload = data as { error?: string; email?: string } | null
      if (payload?.error) {
        setError(payload.error)
        return
      }
      setInfo(
        `Senha de ${resetUsuario.nome} (${payload?.email || resetUsuario.email}) redefinida. Informe a nova senha ao usuário.`,
      )
      setResetUsuario(null)
      setResetForm({ senha: '', confirmar: '' })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao resetar senha')
    } finally {
      setResettingSenha(false)
    }
  }

  async function exportBackup() {
    const { data, error: err } = await supabase.functions.invoke('exportar-backup')
    if (err) {
      // fallback client-side
      const tables = [
        'escolas',
        'rotas',
        'alimentos_macronutrientes',
        'precos',
        'contratos',
        'itens_contrato',
        'cardapios',
        'entregas',
        'entradas_estoque',
        'calendario',
        'configuracoes',
      ]
      const dump: Record<string, unknown> = {}
      for (const t of tables) {
        const { data: rows } = await supabase.from(t).select('*')
        dump[t] = rows
      }
      downloadText(
        `backup-isen-${todayISO()}.json`,
        JSON.stringify(dump, null, 2),
        'application/json',
      )
      return
    }
    downloadText(
      `backup-isen-${todayISO()}.json`,
      JSON.stringify(data, null, 2),
      'application/json',
    )
  }

  async function runDanger(action: string) {
    if (action === 'RESETAR ESCOLAS') {
      const { error: err } = await supabase.from('escolas').delete().neq('id', '00000000-0000-0000-0000-000000000000')
      if (err) throw err
    } else if (action === 'APAGAR ENTREGAS') {
      const { error: errEntradas } = await supabase
        .from('entradas_estoque')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000')
      if (errEntradas) throw errEntradas
      const { error: err } = await supabase.from('entregas').delete().neq('id', '00000000-0000-0000-0000-000000000000')
      if (err) throw err
    } else if (action === 'RESETAR TUDO') {
      for (const t of ['entradas_estoque', 'entregas', 'escolas'] as const) {
        const { error: err } = await supabase.from(t).delete().neq('id', '00000000-0000-0000-0000-000000000000')
        if (err) throw err
      }
    }
    await supabase.from('log_auditoria').insert({
      usuario_id: profile?.id,
      acao: 'reset',
      tabela: action,
      registro_id: null,
      dados_anteriores: null,
      dados_novos: { action },
    })
    load()
  }

  function selectTab(next: Tab) {
    setTab(next)
    setSearchParams(next === 'geral' ? {} : { tab: next }, { replace: true })
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'geral', label: 'Geral' },
    { id: 'macronutrientes', label: 'Macronutrientes' },
    { id: 'valores_referencia', label: 'Valores de referência' },
    { id: 'precos', label: 'Tabela de Preços' },
    { id: 'rotas', label: 'Rotas' },
    { id: 'fornecedores', label: 'Fornecedores' },
    { id: 'backup', label: 'Backup & Dados' },
    { id: 'escolas', label: 'Escolas' },
    { id: 'escolas_ceact', label: 'Escolas CEACT' },
    { id: 'usuarios', label: 'Usuários' },
  ]

  return (
    <div
      className={
        tab === 'escolas' || tab === 'escolas_ceact'
          ? 'configuracoes-page configuracoes-page--escolas'
          : 'configuracoes-page'
      }
    >
      <div className="page-header">
        <h1>Configurações</h1>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {info && <div className="alert alert-info">{info}</div>}
      <div className="tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => selectTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'geral' && (
        <div className="card">
          {!cfg && (
            <div className="alert alert-info">
              Nenhuma configuração encontrada. Preencha e salve para criar o registro singleton.
            </div>
          )}
          <div className="form-grid">
            {(
              [
                ['municipio', 'Município'],
                ['estado', 'Estado'],
                ['secretaria', 'Secretaria'],
                ['nutricionista_responsavel', 'Nutricionista'],
                ['fiscal_contrato', 'Fiscal do contrato'],
                ['fornecedor_atual', 'Fornecedor'],
                ['cnpj_fornecedor', 'CNPJ fornecedor'],
              ] as const
            ).map(([key, label]) => (
              <div className="field" key={key}>
                <label>{label}</label>
                <input
                  value={((cfg?.[key] as string) || '')}
                  onChange={(e) =>
                    setCfg({
                      id: 1,
                      municipio: null,
                      estado: null,
                      secretaria: null,
                      nutricionista_responsavel: null,
                      ano_letivo: 2026,
                      fiscal_contrato: null,
                      fornecedor_atual: null,
                      cnpj_fornecedor: null,
                      ...cfg,
                      [key]: e.target.value,
                    })
                  }
                />
              </div>
            ))}
            <div className="field">
              <label>Ano letivo</label>
              <input
                type="number"
                value={cfg?.ano_letivo || 2026}
                onChange={(e) =>
                  setCfg({
                    id: 1,
                    municipio: null,
                    estado: null,
                    secretaria: null,
                    nutricionista_responsavel: null,
                    fiscal_contrato: null,
                    fornecedor_atual: null,
                    cnpj_fornecedor: null,
                    ...cfg,
                    ano_letivo: Number(e.target.value),
                  })
                }
              />
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <button type="button" className="btn btn-primary" onClick={saveCfg}>
              Salvar
            </button>
          </div>
        </div>
      )}

      {tab === 'macronutrientes' && (
        <MacronutrientesSection onError={setError} onInfo={setInfo} />
      )}

      {tab === 'valores_referencia' && (
        <ValoresReferenciaSection onError={setError} onInfo={setInfo} />
      )}

      {tab === 'precos' && (
        <div>
          {canWrite('precos') && (
            <p className="kpi-sub" style={{ marginBottom: 12 }}>
              Para Kg, g e ml, informe as gramas por unidade comercial (1 Kg = 1000 g). Para UN
              (ex.: ovos), informe un × gramas (30 × 50 = 1500 g). Para Maço, informe apenas a
              quantidade em gramas de 1 maço. Alterações são salvas
              ao sair do campo. O preço por 100g usa o VALOR UNITÁRIO (R$) do contrato.
            </p>
          )}
          <div className="page-actions" style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {canWrite('precos') && (
              <button type="button" className="btn btn-secondary" onClick={openNovoProdutoModal}>
                Novo produto
              </button>
            )}
          </div>
          <div className="filters">
            <div className="field">
              <label>Produto</label>
              <select
                value={precoForm.produto_id}
                onChange={(e) => setPrecoForm({ ...precoForm, produto_id: e.target.value })}
              >
                <option value="">Selecione</option>
                {produtos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>VALOR UNITÁRIO (R$)</label>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={precoForm.valor_unitario}
                onChange={(e) => setPrecoForm({ ...precoForm, valor_unitario: e.target.value })}
                onBlur={(e) => {
                  const v = e.target.value.trim()
                  if (v) {
                    setPrecoForm({
                      ...precoForm,
                      valor_unitario: formatCurrencyInput(parseCurrencyBr(v)),
                    })
                  }
                }}
              />
            </div>
            <div className="field">
              <label>Vigência</label>
              <input
                type="date"
                value={precoForm.vigencia_inicio}
                onChange={(e) => setPrecoForm({ ...precoForm, vigencia_inicio: e.target.value })}
              />
            </div>
            <button type="button" className="btn btn-primary" onClick={savePreco}>
              Adicionar preço
            </button>
          </div>
          <Modal
            open={produtoModalOpen}
            title="Novo produto"
            onClose={closeNovoProdutoModal}
            footer={
              <>
                <button type="button" className="btn btn-secondary" onClick={closeNovoProdutoModal}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => void saveNovoProduto()}
                  disabled={savingProduto}
                >
                  {savingProduto ? 'Salvando…' : 'Cadastrar'}
                </button>
              </>
            }
          >
            <div className="form-grid">
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Nome do produto *</label>
                <input
                  value={produtoForm.nome}
                  onChange={(e) =>
                    setProdutoForm((prev) => ({ ...prev, nome: e.target.value }))
                  }
                  placeholder="Ex.: Arroz tipo 1"
                  autoFocus
                />
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>Fornecedor *</label>
                <select
                  value={produtoForm.fornecedor_id}
                  onChange={(e) =>
                    setProdutoForm((prev) => ({ ...prev, fornecedor_id: e.target.value }))
                  }
                >
                  <option value="">Selecione</option>
                  {fornecedores.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.razao_social || f.nome}
                    </option>
                  ))}
                </select>
                {!fornecedores.length && (
                  <span className="kpi-sub">
                    Nenhum fornecedor ativo. Cadastre em Configurações → Fornecedores.
                  </span>
                )}
              </div>
            </div>
          </Modal>
          <p className="kpi-sub" style={{ marginBottom: 12 }}>
            Um registro por produto com o preço vigente. Clique no produto para ver o histórico de
            alterações (ordem de inserção). Editar preço ou vigência na linha principal cria um novo
            registro — a última inserção prevalece quando a vigência coincide.
          </p>
          <div className="table-wrap">
            <table className="data-table data-table-precos">
              <thead>
                <tr>
                  <SortableTh
                    label="Produto"
                    sortKey="produto"
                    activeKey={precosSort.sortKey}
                    direction={precosSort.sortDir}
                    onSort={precosSort.toggleSort}
                    col="name"
                  />
                  <SortableTh
                    label="UN"
                    sortKey="unidade"
                    activeKey={precosSort.sortKey}
                    direction={precosSort.sortDir}
                    onSort={precosSort.toggleSort}
                    col="text"
                  />
                  <SortableTh
                    label="Gramas/un"
                    sortKey="gramas"
                    activeKey={precosSort.sortKey}
                    direction={precosSort.sortDir}
                    onSort={precosSort.toggleSort}
                    col="currency"
                  />
                  <SortableTh
                    label="VALOR UNITÁRIO (R$)"
                    sortKey="valor"
                    activeKey={precosSort.sortKey}
                    direction={precosSort.sortDir}
                    onSort={precosSort.toggleSort}
                    col="currency"
                  />
                  <th
                    className="col-currency"
                    title="(Valor unitário ÷ gramas da embalagem) × 100. Frascos menores que 100 g geram valor proporcionalmente maior."
                  >
                    Preço 100g (R$)
                  </th>
                  <SortableTh
                    label="Vigência"
                    sortKey="vigencia"
                    activeKey={precosSort.sortKey}
                    direction={precosSort.sortDir}
                    onSort={precosSort.toggleSort}
                    col="date"
                  />
                  <SortableTh
                    label="Fornecedor"
                    sortKey="fornecedor"
                    activeKey={precosSort.sortKey}
                    direction={precosSort.sortDir}
                    onSort={precosSort.toggleSort}
                    col="text"
                    className="col-fornecedor"
                  />
                </tr>
              </thead>
              <tbody>
                {precosGrouped.map((grupo) => {
                  const expanded = precosExpandidos.has(grupo.produtoId)
                  const valido = grupo.valido
                  const inputKey = `${grupo.produtoId}-${valido?.id ?? 'none'}`
                  const produto = produtoById.get(grupo.produtoId)
                  const draft = produto ? getConversaoDraft(produto) : undefined
                  const unidadeEfetiva = draft?.unidade ?? produto?.unidade ?? ''
                  const modoConversao = modoConversaoDoDraft(produto, draft)
                  const totalGramasDraft = draft ? totalGramasDoDraft(produto, draft) : null
                  const placeholderGramas =
                    unidadeEfetiva.toLowerCase() === 'ml' ? 'ml' : 'Gramas'
                  const preco100g = textoPreco100g(
                    produto,
                    valido ? Number(valido.valor_unitario) : null,
                    draft,
                  )
                  return (
                    <Fragment key={grupo.produtoId}>
                      <tr
                        className={`preco-produto-row${expanded ? ' preco-produto-row--expanded' : ''}`}
                        onClick={() => togglePrecoExpandido(grupo.produtoId)}
                        style={{ cursor: grupo.historico.length ? 'pointer' : undefined }}
                      >
                        <td className="col-name">
                          {grupo.historico.length > 0 && (
                            <span className="preco-expand-icon" aria-hidden>
                              {expanded ? '▾' : '▸'}
                            </span>
                          )}
                          {grupo.produtoNome}
                        </td>
                        <td className="col-text" onClick={(e) => e.stopPropagation()}>
                          {produto ? (
                            canWrite('precos') ? (
                              <select
                                className="preco-un-select"
                                value={draft?.unidade ?? produto.unidade}
                                onChange={(e) =>
                                  patchConversaoDraft(grupo.produtoId, { unidade: e.target.value })
                                }
                                onBlur={() => onConversaoBlur(grupo.produtoId)}
                              >
                                {[...new Set([...UNIDADES_COMERCIAIS, produto.unidade])].map((u) => (
                                  <option key={u} value={u}>
                                    {formatUnidadeExibicao(u)}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              formatUnidadeExibicao(produto.unidade)
                            )
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="col-currency col-preco-conversao" onClick={(e) => e.stopPropagation()}>
                          {produto ? (
                            canWrite('precos') ? (
                              modoConversao === 'embalagem' ? (
                                <div className="preco-gramas-cell">
                                  <div className="preco-gramas-row">
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      className="input-table-currency preco-qtd-input"
                                      placeholder="un"
                                      title="Quantidade de unidades na embalagem"
                                      value={draft?.unidades_por_embalagem ?? ''}
                                      onChange={(e) =>
                                        patchConversaoDraft(grupo.produtoId, {
                                          unidades_por_embalagem: e.target.value,
                                        })
                                      }
                                      onBlur={() => onConversaoBlur(grupo.produtoId)}
                                    />
                                    <span className="preco-gramas-times" aria-hidden>
                                      ×
                                    </span>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      className="input-table-currency preco-gramas-input"
                                      placeholder="gramas"
                                      title="Peso em gramas de cada unidade"
                                      value={draft?.gramas_por_item ?? ''}
                                      onChange={(e) =>
                                        patchConversaoDraft(grupo.produtoId, {
                                          gramas_por_item: e.target.value,
                                        })
                                      }
                                      onBlur={() => onConversaoBlur(grupo.produtoId)}
                                    />
                                  </div>
                                  {totalGramasDraft != null && (
                                    <span className="preco-gramas-total" title="Total em gramas">
                                      = {formatGramas(totalGramasDraft)} g
                                    </span>
                                  )}
                                </div>
                              ) : modoConversao === 'maco' ? (
                                <div className="preco-gramas-cell">
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    className="input-table-currency preco-gramas-input preco-gramas-input--full"
                                    placeholder="Qtd (g)"
                                    title="Peso em gramas de 1 maço"
                                    value={draft?.gramas_por_unidade ?? ''}
                                    onChange={(e) =>
                                      patchConversaoDraft(grupo.produtoId, {
                                        gramas_por_unidade: e.target.value,
                                      })
                                    }
                                    onBlur={() => onConversaoBlur(grupo.produtoId)}
                                  />
                                </div>
                              ) : (
                                <div className="preco-gramas-cell">
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    className="input-table-currency preco-gramas-input preco-gramas-input--full"
                                    placeholder={placeholderGramas}
                                    title={
                                      unidadeEfetiva.toLowerCase() === 'ml'
                                        ? 'Volume em ml da unidade comercial'
                                        : 'Gramas por unidade comercial'
                                    }
                                    value={draft?.gramas_por_unidade ?? ''}
                                    onChange={(e) =>
                                      patchConversaoDraft(grupo.produtoId, {
                                        gramas_por_unidade: e.target.value,
                                      })
                                    }
                                    onBlur={() => onConversaoBlur(grupo.produtoId)}
                                  />
                                </div>
                              )
                            ) : (
                              textoConversaoGramas(produto)
                            )
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="col-currency">
                          {valido ? formatCurrencyInput(Number(valido.valor_unitario)) : '—'}
                        </td>
                        <td className="col-currency">{preco100g}</td>
                        <td className="col-date" onClick={(e) => e.stopPropagation()}>
                          {canWrite('precos') && valido ? (
                            <input
                              key={`${inputKey}-vigencia`}
                              type="date"
                              className="input-table-date"
                              defaultValue={valido.vigencia_inicio}
                              onBlur={(e) => {
                                void onPrecoVigenteBlur(
                                  grupo.produtoId,
                                  valido,
                                  formatCurrencyInput(Number(valido.valor_unitario)),
                                  e.target.value,
                                )
                              }}
                            />
                          ) : (
                            valido ? formatDate(valido.vigencia_inicio) : '—'
                          )}
                        </td>
                        <td className="col-text col-fornecedor" onClick={(e) => e.stopPropagation()}>
                          {canWrite('precos') ? (
                            <select
                              className="preco-fornecedor-select"
                              value={valido?.fornecedor_id ?? ''}
                              onChange={(e) =>
                                void onFornecedorVigenteChange(
                                  grupo.produtoId,
                                  valido,
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">Selecione</option>
                              {fornecedores.map((f) => (
                                <option key={f.id} value={f.id}>
                                  {f.razao_social || f.nome}
                                </option>
                              ))}
                            </select>
                          ) : (
                            valido?.fornecedor || '—'
                          )}
                        </td>
                      </tr>
                      {expanded &&
                        grupo.historico.map((h) => (
                          <tr key={h.id} className="preco-historico-row">
                            <td className="col-name" />
                            <td className="col-text" />
                            <td className="col-currency" />
                            <td className="col-currency">{formatCurrencyInput(Number(h.valor_unitario))}</td>
                            <td className="col-currency">
                              {textoPreco100g(produto, Number(h.valor_unitario))}
                            </td>
                            <td className="col-date">{formatDate(h.vigencia_inicio)}</td>
                            <td className="col-text col-fornecedor">{h.fornecedor || '—'}</td>
                          </tr>
                        ))}
                    </Fragment>
                  )
                })}
                {!precosGrouped.length && (
                  <tr>
                    <td colSpan={7} className="kpi-sub">
                      Nenhum preço cadastrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'rotas' && (
        <div>
          <div className="page-header" style={{ marginBottom: 12, padding: 0 }}>
            <div>
              <h2 style={{ fontSize: 16, margin: 0 }}>Rotas de entrega</h2>
              <p className="kpi-sub" style={{ margin: '4px 0 0' }}>
                Cadastre a rota e vincule as escolas que fazem parte dela.
              </p>
            </div>
            {canWrite('rotas') && (
              <button type="button" className="btn btn-primary" onClick={openCreateRota}>
                Nova rota
              </button>
            )}
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh
                    label="Nome"
                    sortKey="nome"
                    activeKey={rotasSort.sortKey}
                    direction={rotasSort.sortDir}
                    onSort={rotasSort.toggleSort}
                    col="name"
                  />
                  <SortableTh
                    label="Nutricionista"
                    sortKey="responsavel"
                    activeKey={rotasSort.sortKey}
                    direction={rotasSort.sortDir}
                    onSort={rotasSort.toggleSort}
                    col="text"
                  />
                  <SortableTh
                    label="Zona"
                    sortKey="zona"
                    activeKey={rotasSort.sortKey}
                    direction={rotasSort.sortDir}
                    onSort={rotasSort.toggleSort}
                    col="compact"
                  />
                  <SortableTh
                    label="Escolas"
                    sortKey="escolas"
                    activeKey={rotasSort.sortKey}
                    direction={rotasSort.sortDir}
                    onSort={rotasSort.toggleSort}
                    col="num"
                  />
                  <th className="col-actions">Ações</th>
                </tr>
              </thead>
              <tbody>
                {rotasSorted.map((r) => {
                  const expanded = rotaExpandida === r.id
                  const escolasDaRota = escolasCfg.filter((e) => e.rota_id === r.id)
                  return (
                    <Fragment key={r.id}>
                      <tr>
                        <td className="col-name">{r.nome}</td>
                        <td className="col-text">{r.nutricionista?.nome || r.responsavel || '—'}</td>
                        <td className="col-compact">{r.zona}</td>
                        <td className="col-num">{escolasCountByRota.get(r.id) || 0}</td>
                        <td className="col-actions">
                          <div className="table-actions">
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => setRotaExpandida(expanded ? null : r.id)}
                            >
                              {expanded ? 'Ocultar' : 'Ver escolas'}
                            </button>
                            {canWrite('rotas') && (
                              <IconButton
                                variant="edit"
                                label={`Editar ${r.nome}`}
                                onClick={() => openEditRota(r)}
                              />
                            )}
                            {canDelete('rotas') && (
                              <IconButton
                                variant="delete"
                                label={`Excluir ${r.nome}`}
                                onClick={() => setDeleteRotaTarget(r)}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={5} style={{ background: 'var(--color-bg)', padding: 12 }}>
                            {escolasDaRota.length ? (
                              <ul style={{ margin: 0, paddingLeft: 18, columns: 4 }}>
                                {escolasDaRota.map((e) => (
                                  <li key={e.id}>
                                    {e.nome}{' '}
                                    <span className="kpi-sub">({e.total_alunos} alunos)</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="kpi-sub">Nenhuma escola vinculada.</span>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
                {!rotasSorted.length && (
                  <tr>
                    <td colSpan={5} className="kpi-sub">
                      Nenhuma rota cadastrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Modal
            open={rotaModalOpen}
            size="lg"
            title={editingRota ? `Editar rota · ${editingRota.nome}` : 'Nova rota'}
            onClose={cancelEditRota}
            footer={
              <>
                <button type="button" className="btn btn-secondary" onClick={cancelEditRota}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={savingRota}
                  onClick={() => void saveRota()}
                >
                  {savingRota ? 'Salvando…' : 'Salvar rota'}
                </button>
              </>
            }
          >
            {error && rotaModalOpen && <div className="alert alert-error">{error}</div>}
            <div className="form-grid">
              <div className="field">
                <label>Nome *</label>
                <input
                  value={rotaForm.nome}
                  onChange={(e) => setRotaForm({ ...rotaForm, nome: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Nutricionista *</label>
                <select
                  value={rotaForm.nutricionista_id}
                  onChange={(e) => setRotaForm({ ...rotaForm, nutricionista_id: e.target.value })}
                >
                  <option value="">Selecione a nutricionista…</option>
                  {nutricionistas.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome} — {u.email}
                    </option>
                  ))}
                </select>
                {!nutricionistas.length && (
                  <p className="kpi-sub" style={{ marginTop: 6 }}>
                    Nenhum usuário com perfil Nutricionista. Crie em Configurações → Usuários.
                  </p>
                )}
              </div>
              <div className="field">
                <label>Zona *</label>
                <select
                  value={rotaForm.zona}
                  onChange={(e) => setRotaForm({ ...rotaForm, zona: e.target.value as ZonaTipo })}
                >
                  <option value="urbana">Urbana</option>
                  <option value="rural">Rural</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}
              >
                <strong>
                  Escolas da rota{' '}
                  <span className="kpi-sub">({rotaEscolaIds.length} selecionada(s))</span>
                </strong>
                <div className="table-actions">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() =>
                      setRotaEscolaIds((prev) => {
                        const ids = new Set(prev)
                        for (const e of escolasFiltradasRota) ids.add(e.id)
                        return [...ids]
                      })
                    }
                  >
                    Marcar filtradas
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setRotaEscolaIds([])}
                  >
                    Limpar
                  </button>
                </div>
              </div>
              <div className="field" style={{ marginBottom: 8 }}>
                <label>Buscar escola</label>
                <input
                  value={rotaEscolaFiltro}
                  onChange={(e) => setRotaEscolaFiltro(e.target.value)}
                  placeholder="Digite o nome…"
                />
              </div>
              <p className="kpi-sub" style={{ marginBottom: 8 }}>
                Escola já vinculada a outra rota será reatribuída ao salvar.
              </p>
              <div
                className="table-wrap"
                style={{ maxHeight: 280, border: '1px solid var(--color-border)', borderRadius: 8 }}
              >
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }} />
                      <th>Escola</th>
                      <th className="col-text">Rota atual</th>
                      <th className="col-num">Alunos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {escolasFiltradasRota.map((esc) => {
                      const checked = rotaEscolaIds.includes(esc.id)
                      const otherRota =
                        esc.rota_id && esc.rota_id !== editingRota?.id
                          ? rotaNomeById.get(esc.rota_id)
                          : esc.rota_id === editingRota?.id
                            ? 'Esta rota'
                            : null
                      return (
                        <tr key={esc.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRotaEscola(esc.id)}
                              aria-label={`Selecionar ${esc.nome}`}
                            />
                          </td>
                          <td className="col-name">{esc.nome}</td>
                          <td className="col-text">
                            {otherRota || <span className="kpi-sub">Sem rota</span>}
                          </td>
                          <td className="col-num">{esc.total_alunos}</td>
                        </tr>
                      )
                    })}
                    {!escolasFiltradasRota.length && (
                      <tr>
                        <td colSpan={4} className="kpi-sub">
                          Nenhuma escola encontrada.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Modal>
        </div>
      )}

      {tab === 'fornecedores' && (
        <FornecedoresSection
          embedded
          onInfo={(msg) => {
            setError(null)
            setInfo(msg)
          }}
          onError={setError}
        />
      )}

      {tab === 'backup' && (
        <div className="card">
          <h2 style={{ fontSize: 16, marginBottom: 12 }}>Backup</h2>
          <div className="page-actions" style={{ marginBottom: 24 }}>
            <button type="button" className="btn btn-primary" onClick={exportBackup}>
              Exportar backup JSON
            </button>
          </div>
          <h2 style={{ fontSize: 16, marginBottom: 12, color: 'var(--color-danger)' }}>
            Zona de Perigo
          </h2>
          <div className="page-actions">
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setDangerAction('RESETAR ESCOLAS')}
            >
              Resetar escolas
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setDangerAction('APAGAR ENTREGAS')}
            >
              Apagar entregas
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setDangerAction('RESETAR TUDO')}
            >
              Resetar tudo
            </button>
          </div>
        </div>
      )}

      <div className="config-escolas-panel" style={{ display: tab === 'escolas' ? 'flex' : 'none' }}>
        <EscolasSection embedded />
      </div>

      <div className="config-escolas-panel" style={{ display: tab === 'escolas_ceact' ? 'flex' : 'none' }}>
        <EscolasCeactSection embedded />
      </div>

      {tab === 'usuarios' && (
        <div>
          {isLocalDev && (
            <div className="alert alert-warning" style={{ marginBottom: 12 }}>
              Cadastro pela interface exige login Supabase real. Use <code>npm run local:users</code> ou
              faça logout e entre com <code>admin@demo.educae.local</code> após{' '}
              <code>npm run build:local</code>.
            </div>
          )}
          <div className="filters">
            <div className="field">
              <label>Nome *</label>
              <input
                value={invite.nome}
                onChange={(e) => setInvite({ ...invite, nome: e.target.value })}
              />
            </div>
            <div className="field">
              <label>E-mail *</label>
              <input
                type="email"
                value={invite.email}
                onChange={(e) => setInvite({ ...invite, email: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Senha inicial *</label>
              <input
                type="password"
                autoComplete="new-password"
                value={invite.senha}
                onChange={(e) => setInvite({ ...invite, senha: e.target.value })}
                placeholder="Mín. 8 caracteres"
              />
            </div>
            <div className="field">
              <label>Perfil</label>
              <select
                value={invite.perfil}
                onChange={(e) =>
                  setInvite({ ...invite, perfil: e.target.value as PerfilUsuario })
                }
              >
                {(Object.keys(PERFIL_LABELS) as PerfilUsuario[]).map((p) => (
                  <option key={p} value={p}>
                    {PERFIL_LABELS[p]}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={inviting}
              onClick={() => void inviteUser()}
            >
              {inviting ? 'Criando…' : 'Criar usuário'}
            </button>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh
                    label="Nome"
                    sortKey="nome"
                    activeKey={usuariosSort.sortKey}
                    direction={usuariosSort.sortDir}
                    onSort={usuariosSort.toggleSort}
                    col="name"
                  />
                  <SortableTh
                    label="E-mail"
                    sortKey="email"
                    activeKey={usuariosSort.sortKey}
                    direction={usuariosSort.sortDir}
                    onSort={usuariosSort.toggleSort}
                    col="text"
                  />
                  <SortableTh
                    label="Perfil"
                    sortKey="perfil"
                    activeKey={usuariosSort.sortKey}
                    direction={usuariosSort.sortDir}
                    onSort={usuariosSort.toggleSort}
                    col="compact"
                  />
                  <SortableTh
                    label="Ativo"
                    sortKey="ativo"
                    activeKey={usuariosSort.sortKey}
                    direction={usuariosSort.sortDir}
                    onSort={usuariosSort.toggleSort}
                    col="compact"
                  />
                  <th className="col-actions" />
                </tr>
              </thead>
              <tbody>
                {usuariosSorted.map((u) => (
                  <tr key={u.id}>
                    <td className="col-name">{u.nome}</td>
                    <td className="col-text">{u.email}</td>
                    <td className="col-compact">
                      <select
                        value={u.perfil}
                        onChange={(e) => changePerfil(u, e.target.value as PerfilUsuario)}
                      >
                        {(Object.keys(PERFIL_LABELS) as PerfilUsuario[]).map((p) => (
                          <option key={p} value={p}>
                            {PERFIL_LABELS[p]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="col-compact">{u.ativo ? 'Sim' : 'Não'}</td>
                    <td className="col-actions" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => openEditUsuario(u)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => openResetSenha(u)}
                      >
                        Resetar senha
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => toggleUsuario(u)}
                      >
                        {u.ativo ? 'Desativar' : 'Ativar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        open={!!editUsuario}
        title={`Editar usuário${editUsuario ? ` — ${editUsuario.email}` : ''}`}
        onClose={() => !savingUsuario && setEditUsuario(null)}
      >
        {error && editUsuario && <div className="alert alert-error">{error}</div>}
        <div className="filters" style={{ marginBottom: 12 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Nome *</label>
            <input
              value={editForm.nome}
              onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
              disabled={savingUsuario}
            />
          </div>
          <div className="field">
            <label>Perfil</label>
            <select
              value={editForm.perfil}
              onChange={(e) =>
                setEditForm({ ...editForm, perfil: e.target.value as PerfilUsuario })
              }
              disabled={savingUsuario}
            >
              {(Object.keys(PERFIL_LABELS) as PerfilUsuario[]).map((p) => (
                <option key={p} value={p}>
                  {PERFIL_LABELS[p]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Ativo</label>
            <select
              value={editForm.ativo ? '1' : '0'}
              onChange={(e) => setEditForm({ ...editForm, ativo: e.target.value === '1' })}
              disabled={savingUsuario}
            >
              <option value="1">Sim</option>
              <option value="0">Não</option>
            </select>
          </div>
        </div>
        <div className="page-actions" style={{ justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={savingUsuario}
            onClick={() => setEditUsuario(null)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={savingUsuario}
            onClick={() => void salvarUsuario()}
          >
            {savingUsuario ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </Modal>

      <Modal
        open={!!resetUsuario}
        title={`Resetar senha${resetUsuario ? ` — ${resetUsuario.nome}` : ''}`}
        onClose={() => !resettingSenha && setResetUsuario(null)}
      >
        {error && resetUsuario && <div className="alert alert-error">{error}</div>}
        <p className="kpi-sub" style={{ marginBottom: 12 }}>
          Defina uma nova senha (mín. 8 caracteres) e informe ao usuário. E-mail:{' '}
          <strong>{resetUsuario?.email}</strong>
        </p>
        <div className="filters" style={{ marginBottom: 12 }}>
          <div className="field">
            <label>Nova senha *</label>
            <input
              type="password"
              autoComplete="new-password"
              value={resetForm.senha}
              onChange={(e) => setResetForm({ ...resetForm, senha: e.target.value })}
              disabled={resettingSenha}
            />
          </div>
          <div className="field">
            <label>Confirmar senha *</label>
            <input
              type="password"
              autoComplete="new-password"
              value={resetForm.confirmar}
              onChange={(e) => setResetForm({ ...resetForm, confirmar: e.target.value })}
              disabled={resettingSenha}
            />
          </div>
        </div>
        <div className="page-actions" style={{ justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={resettingSenha}
            onClick={() => setResetUsuario(null)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={resettingSenha}
            onClick={() => void resetarSenhaUsuario()}
          >
            {resettingSenha ? 'Salvando…' : 'Resetar senha'}
          </button>
        </div>
      </Modal>

      <ConfirmModal
        open={!!deleteRotaTarget}
        title="Excluir rota"
        description={`Excluir permanentemente "${deleteRotaTarget?.nome}"? Escolas e entregas vinculadas impedem a exclusão.`}
        confirmLabel="Excluir"
        variant="danger"
        onClose={() => setDeleteRotaTarget(null)}
        onConfirm={deleteRota}
      />

      <ConfirmDangerModal
        open={!!dangerAction}
        title="Zona de Perigo"
        actionName={dangerAction || ''}
        description="Esta ação é destrutiva e será registrada na auditoria."
        onClose={() => setDangerAction(null)}
        onConfirm={async () => {
          if (dangerAction) await runDanger(dangerAction)
        }}
      />
    </div>
  )
}
